import React, { useEffect, useMemo, useState } from "react";
import API_URL from "../../apiConfig";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useAuth } from "../../context/AuthContext";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    unsubscribed: 0,
    spanish: 0,
    french: 0,
    with_consent: 0,
  });

  const [newsletterIssues, setNewsletterIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [selectedIssueId, setSelectedIssueId] = useState("");
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState("");

  const [loading, setLoading] = useState(true);
  const [subscribersLoading, setSubscribersLoading] = useState(true);
  const [activeView, setActiveView] = useState("members");
  const [search, setSearch] = useState("");
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const [subscriberStatus, setSubscriberStatus] = useState("all");
  const [subscriberLanguage, setSubscriberLanguage] = useState("all");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [creating, setCreating] = useState(false);

  const navigate = useNavigate();
  const { logout } = useAuth();

  const filteredMembers = useMemo(() => {
    const term = search.toLowerCase().trim();

    if (!term) return members;

    return members.filter((m) => {
      return (
        m.email?.toLowerCase().includes(term) ||
        m.name?.toLowerCase().includes(term)
      );
    });
  }, [members, search]);

  const filteredSubscribers = useMemo(() => {
    const term = subscriberSearch.toLowerCase().trim();

    return subscribers.filter((subscriber) => {
      const matchesTerm = !term ||
        subscriber.email?.toLowerCase().includes(term) ||
        subscriber.name?.toLowerCase().includes(term) ||
        subscriber.source?.toLowerCase().includes(term);

      const matchesStatus = subscriberStatus === "all" || subscriber.status === subscriberStatus;
      const matchesLanguage = subscriberLanguage === "all" || subscriber.language === subscriberLanguage;

      return matchesTerm && matchesStatus && matchesLanguage;
    });
  }, [subscribers, subscriberSearch, subscriberStatus, subscriberLanguage]);

  const publishedNewsletterIssues = useMemo(() => {
    return newsletterIssues.filter((issue) => {
      const isPublished = issue.status === "published";
      const isNewsletter = !issue.type || issue.type === "newsletter";
      const notSentYet = !issue.newsletter_sent_at;
      return isPublished && isNewsletter && notSentYet;
    });
  }, [newsletterIssues]);

  const selectedIssue = useMemo(() => {
    return publishedNewsletterIssues.find((issue) => String(issue.id) === String(selectedIssueId));
  }, [publishedNewsletterIssues, selectedIssueId]);

  useEffect(() => {
    loadMembers();
    loadSubscribers();
    loadNewsletterIssues();
  }, []);

  useEffect(() => {
    const selectedStillAvailable = publishedNewsletterIssues.some(
      (issue) => String(issue.id) === String(selectedIssueId)
    );

    if (!publishedNewsletterIssues.length) {
      if (selectedIssueId) setSelectedIssueId("");
      return;
    }

    if (!selectedIssueId || !selectedStillAvailable) {
      setSelectedIssueId(String(publishedNewsletterIssues[0].id));
    }
  }, [publishedNewsletterIssues, selectedIssueId]);

  function getSessionToken() {
    const session = JSON.parse(localStorage.getItem("zedro_session"));
    return session?.token;
  }

  async function loadMembers() {
    try {
      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setMembers(json.members || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadSubscribers() {
    try {
      setSubscribersLoading(true);
      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/newsletter/subscribers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setSubscribers(json.subscribers || []);
        setSubscriberStats(json.stats || {
          total: 0,
          active: 0,
          unsubscribed: 0,
          spanish: 0,
          french: 0,
          with_consent: 0,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubscribersLoading(false);
    }
  }

  async function loadNewsletterIssues() {
    try {
      setIssuesLoading(true);
      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/newsletter/issues`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "error_loading_newsletter_issues");
      }

      setNewsletterIssues(Array.isArray(json.items) ? json.items : []);
    } catch (err) {
      console.error(err);
      setNewsletterIssues([]);
    } finally {
      setIssuesLoading(false);
    }
  }

  async function sendNewsletterToActiveSubscribers() {
    if (!selectedIssueId || !selectedIssue) {
      alert("Selecciona una edición publicada antes de enviar.");
      return;
    }

    if (!sendConfirmation) {
      alert("Confirma que deseas enviar esta edición solo a suscriptores activos.");
      return;
    }

    if (!subscriberStats.active) {
      alert("No hay suscriptores activos para enviar esta edición.");
      return;
    }

    const confirmed = window.confirm(
      `Vas a enviar ${selectedIssue.issue_line || selectedIssue.hero_title || "esta edición"} a ${subscriberStats.active} suscriptor(es) activo(s). ¿Confirmas el envío?`
    );

    if (!confirmed) return;

    try {
      setSendingNewsletter(true);
      setSendError("");
      setSendResult(null);

      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/email/send-editorial/${selectedIssueId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok) {
        throw new Error(
          json?.error === "editorial_already_sent"
            ? "Esta edición ya fue enviada por correo. Actualiza la página para ver solo ediciones pendientes."
            : json?.error || "error_sending_newsletter"
        );
      }

      setSendResult(json);
      setSendConfirmation(false);
      await loadNewsletterIssues();
    } catch (err) {
      console.error(err);
      setSendError(err.message || "No se pudo enviar ZEDRO News.");
    } finally {
      setSendingNewsletter(false);
    }
  }

  async function createMember(e) {
    e.preventDefault();

    try {
      setCreating(true);
      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/members/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          name,
          role,
        }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "error_creating_member");
      }

      setEmail("");
      setName("");
      setRole("MEMBER");

      loadMembers();
    } catch (err) {
      console.error(err);
      alert("Error creando miembro");
    } finally {
      setCreating(false);
    }
  }

  async function updateRole(userId, newRole) {
    try {
      const token = getSessionToken();

      const res = await fetch(`${API_URL}/api/admin/members/${userId}/role`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "error_updating_role");
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.id === userId ? { ...m, role: newRole } : m
        )
      );
    } catch (err) {
      console.error(err);
      alert("Error actualizando rol");
    }
  }

  function formatDate(value) {
    if (!value) return "—";

    try {
      return new Date(value).toLocaleDateString("es-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  }

  function getStatusLabel(status) {
    if (status === "active") return "Activo";
    if (status === "unsubscribed") return "Baja";
    return status || "—";
  }

  function getLanguageLabel(language) {
    if (language === "fr") return "Francés";
    if (language === "es") return "Español";
    return language || "—";
  }

  if (loading) {
    return <div style={{ padding: 40 }}>Cargando miembros...</div>;
  }

  return (
    <>
      <AdminHeader />

      <div className="admin-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <AdminPageHeader
          title="CRM ZEDRO"
          subtitle="Gestión de usuarios, suscriptores y atención comercial."
          backTo="/admin"
        />

        <section style={styles.tabsWrap}>
          <button
            type="button"
            onClick={() => setActiveView("members")}
            style={{
              ...styles.tabButton,
              ...(activeView === "members" ? styles.tabButtonActive : {}),
            }}
          >
            Usuarios de plataforma
          </button>

          <button
            type="button"
            onClick={() => setActiveView("newsletter")}
            style={{
              ...styles.tabButton,
              ...(activeView === "newsletter" ? styles.tabButtonActive : {}),
            }}
          >
            Suscriptores Newsletter
          </button>

          <button
            type="button"
            onClick={() => navigate("/admin/suscripciones")}
            style={styles.secondaryButton}
          >
            Ver suscripciones
          </button>
        </section>

        {activeView === "members" && (
          <>
            <input
              type="text"
              placeholder="Buscar por email o nombre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />

            <form onSubmit={createMember} style={styles.createCard}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>
                Crear usuario
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={styles.formInput}
                />

                <input
                  placeholder="Nombre"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={styles.formInput}
                />

                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="MEMBER">Member</option>
                  <option value="STAFF">Staff</option>
                  <option value="ADMIN">Admin</option>
                </select>

                <button
                  type="submit"
                  disabled={creating}
                  style={styles.primaryButton}
                >
                  Crear
                </button>
              </div>
            </form>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Nombre</th>
                    <th style={styles.th}>Rol</th>
                    <th style={styles.th}>Producto</th>
                    <th style={styles.th}>Fecha compra</th>
                    <th style={styles.th}>Progreso Core</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredMembers.map((m) => (
                    <tr
                      key={m.id}
                      style={styles.clickableRow}
                      onClick={() => navigate(`/admin/miembros/${m.id}`)}
                    >
                      <td style={styles.td}>{m.email}</td>
                      <td style={styles.td}>{m.name}</td>

                      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                        <select
                          value={m.role}
                          onChange={(e) => updateRole(m.id, e.target.value)}
                          style={styles.smallSelect}
                        >
                          <option value="MEMBER">Member</option>
                          <option value="STAFF">Staff</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </td>

                      <td style={styles.td}>{m.product_name || "-"}</td>
                      <td style={styles.td}>{formatDate(m.purchase_date)}</td>
                      <td style={styles.td}>{m.completed_lessons || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeView === "newsletter" && (
          <>
            <section style={styles.statsGrid}>
              <StatCard label="Total" value={subscriberStats.total} />
              <StatCard label="Activos" value={subscriberStats.active} />
              <StatCard label="Bajas" value={subscriberStats.unsubscribed} />
              <StatCard label="Español" value={subscriberStats.spanish} />
              <StatCard label="Francés" value={subscriberStats.french} />
              <StatCard label="Con consentimiento" value={subscriberStats.with_consent} />
            </section>

            <section style={styles.sendCard}>
              <div style={styles.sendHeader}>
                <div>
                  <h2 style={styles.sendTitle}>Enviar ZEDRO News</h2>
                  <p style={styles.sendText}>
                    El envío se realiza únicamente a suscriptores activos. Las bajas no reciben este correo. Las ediciones ya enviadas no vuelven a aparecer en esta lista.
                  </p>
                </div>

                <span style={styles.sendCountBadge}>
                  {subscriberStats.active || 0} activos
                </span>
              </div>

              <div style={styles.sendGrid}>
                <label style={styles.sendLabel}>
                  Edición publicada pendiente de envío
                  <select
                    value={selectedIssueId}
                    onChange={(e) => {
                      setSelectedIssueId(e.target.value);
                      setSendResult(null);
                      setSendError("");
                    }}
                    disabled={issuesLoading || sendingNewsletter || !publishedNewsletterIssues.length}
                    style={styles.fullInput}
                  >
                    {issuesLoading && <option>Cargando ediciones…</option>}
                    {!issuesLoading && !publishedNewsletterIssues.length && (
                      <option value="">No hay ediciones publicadas pendientes de envío</option>
                    )}
                    {!issuesLoading && publishedNewsletterIssues.map((issue) => (
                      <option key={issue.id} value={issue.id}>
                        {issue.issue_line || issue.hero_title || `Edición ${issue.id}`}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={sendNewsletterToActiveSubscribers}
                  disabled={
                    sendingNewsletter ||
                    issuesLoading ||
                    !selectedIssueId ||
                    !sendConfirmation ||
                    !subscriberStats.active
                  }
                  style={{
                    ...styles.sendButton,
                    ...(
                      sendingNewsletter ||
                      issuesLoading ||
                      !selectedIssueId ||
                      !sendConfirmation ||
                      !subscriberStats.active
                        ? styles.sendButtonDisabled
                        : {}
                    ),
                  }}
                >
                  {sendingNewsletter ? "Enviando…" : "Enviar a activos"}
                </button>
              </div>

              {selectedIssue && (
                <div style={styles.selectedIssueBox}>
                  <small style={styles.previewLabel}>Vista previa de la edición seleccionada</small>
                  <strong>{selectedIssue.issue_line || "Edición seleccionada"}</strong>
                  <span>{selectedIssue.hero_title || selectedIssue.slug}</span>
                </div>
              )}

              <label style={styles.confirmRow}>
                <input
                  type="checkbox"
                  checked={sendConfirmation}
                  onChange={(e) => setSendConfirmation(e.target.checked)}
                  disabled={sendingNewsletter || !selectedIssueId || !subscriberStats.active}
                />
                Confirmo enviar esta edición una sola vez, solo a la base activa de ZEDRO News.
              </label>

              {sendResult && (
                <div style={styles.successCard}>
                  Envío completado. Correos enviados: <strong>{sendResult.sent || 0}</strong>. Esta edición ya no aparecerá como pendiente de envío.
                </div>
              )}

              {sendError && (
                <div style={styles.errorCard}>
                  No se pudo completar el envío: {sendError}
                </div>
              )}
            </section>

            <section style={styles.newsletterFilters}>
              <input
                type="text"
                placeholder="Buscar por email, nombre u origen"
                value={subscriberSearch}
                onChange={(e) => setSubscriberSearch(e.target.value)}
                style={styles.fullInput}
              />

              <select
                value={subscriberStatus}
                onChange={(e) => setSubscriberStatus(e.target.value)}
                style={styles.fullInput}
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="unsubscribed">Dados de baja</option>
              </select>

              <select
                value={subscriberLanguage}
                onChange={(e) => setSubscriberLanguage(e.target.value)}
                style={styles.fullInput}
              >
                <option value="all">Todos los idiomas</option>
                <option value="es">Español</option>
                <option value="fr">Francés</option>
              </select>
            </section>

            <div style={styles.noticeCard}>
              Esta base es la referencia operativa para futuros envíos de ZEDRO News. El token de baja no se muestra en CRM por seguridad.
            </div>

            {subscribersLoading ? (
              <div style={{ padding: 24 }}>Cargando suscriptores...</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeaderRow}>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Nombre</th>
                      <th style={styles.th}>Estado</th>
                      <th style={styles.th}>Idioma</th>
                      <th style={styles.th}>Origen</th>
                      <th style={styles.th}>Consentimiento</th>
                      <th style={styles.th}>Alta</th>
                      <th style={styles.th}>Baja</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredSubscribers.map((subscriber) => (
                      <tr key={subscriber.id || subscriber.email} style={styles.row}>
                        <td style={styles.td}>{subscriber.email}</td>
                        <td style={styles.td}>{subscriber.name || "—"}</td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.badge,
                              ...(subscriber.status === "active"
                                ? styles.badgeGreen
                                : styles.badgeNeutral),
                            }}
                          >
                            {getStatusLabel(subscriber.status)}
                          </span>
                        </td>
                        <td style={styles.td}>{getLanguageLabel(subscriber.language)}</td>
                        <td style={styles.td}>{subscriber.source || "—"}</td>
                        <td style={styles.td}>
                          {subscriber.consent_at ? formatDate(subscriber.consent_at) : "—"}
                        </td>
                        <td style={styles.td}>{formatDate(subscriber.created_at)}</td>
                        <td style={styles.td}>{formatDate(subscriber.unsubscribed_at)}</td>
                      </tr>
                    ))}

                    {!filteredSubscribers.length && (
                      <tr>
                        <td colSpan="8" style={{ ...styles.td, textAlign: "center", padding: 24 }}>
                          No hay suscriptores para mostrar con estos filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value ?? 0}</div>
    </div>
  );
}

const styles = {
  tabsWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  tabButton: {
    minHeight: 40,
    padding: "8px 14px",
    borderRadius: 999,
    border: "1px solid #d9e2ec",
    background: "#fff",
    color: "#0f2f46",
    cursor: "pointer",
    fontWeight: 700,
  },
  tabButtonActive: {
    background: "#0f2f46",
    color: "#fff",
    borderColor: "#0f2f46",
  },
  secondaryButton: {
    minHeight: 40,
    padding: "8px 14px",
    borderRadius: 999,
    border: "1px solid #d9e2ec",
    background: "#fff",
    color: "#0f2f46",
    cursor: "pointer",
    fontWeight: 700,
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    maxWidth: 420,
    marginBottom: 20,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e6ebf1",
    fontSize: 14,
  },
  createCard: {
    marginBottom: 30,
    padding: 20,
    border: "1px solid #e6ebf1",
    borderRadius: 12,
    background: "#fff",
  },
  formInput: {
    padding: 8,
    minHeight: 38,
    border: "1px solid #cfd8e3",
    borderRadius: 6,
  },
  formSelect: {
    padding: 8,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  primaryButton: {
    padding: "8px 16px",
    background: "#0a2230",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 700,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  statCard: {
    padding: 16,
    borderRadius: 14,
    background: "#fff",
    border: "1px solid #e6ebf1",
    boxShadow: "0 10px 26px rgba(15,47,70,.04)",
  },
  statLabel: {
    color: "#6b7a8a",
    fontSize: 12,
    marginBottom: 8,
  },
  statValue: {
    color: "#0f2f46",
    fontSize: 24,
    fontWeight: 800,
  },
  sendCard: {
    padding: 18,
    marginBottom: 18,
    borderRadius: 18,
    background: "linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%)",
    border: "1px solid #dce6ef",
    boxShadow: "0 14px 34px rgba(15,47,70,.06)",
  },
  sendHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 14,
  },
  sendTitle: {
    margin: "0 0 5px",
    color: "#0f2f46",
    fontSize: 20,
    letterSpacing: "-.03em",
  },
  sendText: {
    margin: 0,
    color: "#607086",
    fontSize: 13,
    lineHeight: 1.5,
  },
  sendCountBadge: {
    flex: "0 0 auto",
    minHeight: 30,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 999,
    background: "#eef7ff",
    border: "1px solid #cfe6f7",
    color: "#0f2f46",
    fontSize: 12,
    fontWeight: 900,
  },
  sendGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1fr) auto",
    gap: 12,
    alignItems: "end",
  },
  sendLabel: {
    display: "grid",
    gap: 7,
    color: "#506176",
    fontSize: 12,
    fontWeight: 800,
  },
  sendButton: {
    minHeight: 40,
    padding: "0 18px",
    borderRadius: 999,
    border: 0,
    background: "#0f2f46",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  sendButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  selectedIssueBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "#fff",
    border: "1px solid #e6ebf1",
    display: "grid",
    gap: 4,
    color: "#0f2f46",
    fontSize: 13,
  },
  confirmRow: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    gap: 9,
    color: "#506176",
    fontSize: 13,
    lineHeight: 1.45,
  },
  successCard: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#116b34",
    fontSize: 13,
  },
  errorCard: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontSize: 13,
  },
  newsletterFilters: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1fr) 220px 220px",
    gap: 10,
    marginBottom: 14,
  },
  fullInput: {
    width: "100%",
    minHeight: 40,
    boxSizing: "border-box",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid #d9e2ec",
    background: "#fff",
    color: "#0f2f46",
  },
  noticeCard: {
    padding: 14,
    marginBottom: 18,
    borderRadius: 12,
    background: "#fffaf0",
    border: "1px solid rgba(212,176,98,.35)",
    color: "#5b4a1c",
    fontSize: 13,
    lineHeight: 1.5,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 760,
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
  },
  tableHeaderRow: {
    textAlign: "left",
    borderBottom: "1px solid #eee",
  },
  th: {
    padding: "13px 10px",
    color: "#506176",
    fontSize: 12,
    fontWeight: 800,
  },
  td: {
    padding: "13px 10px",
    borderBottom: "1px solid #f0f3f7",
    color: "#0f2f46",
    fontSize: 13,
    verticalAlign: "middle",
  },
  row: {
    background: "#fff",
  },
  clickableRow: {
    cursor: "pointer",
    borderBottom: "1px solid #f0f3f7",
  },
  smallSelect: {
    padding: 7,
    borderRadius: 6,
    border: "1px solid #cfd8e3",
    background: "#fff",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 26,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },
  badgeGreen: {
    background: "#e9fbef",
    color: "#116b34",
    border: "1px solid #bdebc9",
  },
  badgeNeutral: {
    background: "#f4f6f8",
    color: "#526173",
    border: "1px solid #d9e2ec",
  },
};
